import { CheckCircle2, CircleAlert, Info, X } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

type ToastTone = 'success' | 'error' | 'info'
type ToastItem = { id: number; title: string; message?: string; tone: ToastTone }

const ToastContext = createContext<{ notify: (toast: Omit<ToastItem, 'id'>) => void } | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: number) => setItems((current) => current.filter((item) => item.id !== id)), [])
  const notify = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = Date.now() + Math.random()
    setItems((current) => [...current, { ...toast, id }])
    window.setTimeout(() => dismiss(id), 4200)
  }, [dismiss])
  const value = useMemo(() => ({ notify }), [notify])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite" aria-label="Thông báo hệ thống">
        {items.map((item) => {
          const Icon = item.tone === 'success' ? CheckCircle2 : item.tone === 'error' ? CircleAlert : Info
          return (
            <div className={`toast toast--${item.tone}`} key={item.id} role="status">
              <Icon size={19} aria-hidden="true" />
              <div><strong>{item.title}</strong>{item.message && <p>{item.message}</p>}</div>
              <button className="icon-button" onClick={() => dismiss(item.id)} aria-label="Đóng thông báo"><X size={16} /></button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast phải nằm trong ToastProvider')
  return context
}
